import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface CreateGroupModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (groupName: string) => void;
  isLoading?: boolean;
  error?: string;
}

export function CreateGroupModal({ isOpen, onClose, onSubmit, isLoading = false, error }: CreateGroupModalProps) {
  const [groupName, setGroupName] = useState("");
  const [validationError, setValidationError] = useState("");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!groupName.trim()) {
      setValidationError("Group name is required");
      return;
    }

    if (groupName.trim().length < 3) {
      setValidationError("Group name must be at least 3 characters");
      return;
    }

    if (groupName.trim().length > 50) {
      setValidationError("Group name must be less than 50 characters");
      return;
    }

    setValidationError("");
    onSubmit(groupName.trim());
  };

  const handleClose = () => {
    if (!isLoading) {
      setGroupName("");
      setValidationError("");
      onClose();
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setGroupName(e.target.value);
    setValidationError("");
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Create New Group</DialogTitle>
          <DialogDescription>
            Give your group a name to help members identify it. You'll automatically become the group admin.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="groupName">Group Name</Label>
            <Input
              id="groupName"
              type="text"
              placeholder="Enter group name"
              value={groupName}
              onChange={handleInputChange}
              disabled={isLoading}
              maxLength={50}
              autoFocus
            />
            <p className="text-xs text-muted-foreground">
              {groupName.length}/50 characters
            </p>
          </div>

          {(validationError || error) && (
            <Alert variant="destructive">
              <AlertDescription>
                {validationError || error}
              </AlertDescription>
            </Alert>
          )}

          <div className="flex gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isLoading}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isLoading || !groupName.trim() || groupName.trim().length < 3}
              className="flex-1"
            >
              {isLoading ? "Creating..." : "Create Group"}
            </Button>
          </div>
        </form>

        <div className="text-xs text-muted-foreground pt-4 border-t">
          <p>As the group creator, you'll be able to:</p>
          <ul className="list-disc list-inside mt-1 space-y-1">
            <li>Generate invite codes</li>
            <li>Revoke invite codes</li>
            <li>Manage group members</li>
          </ul>
        </div>
      </DialogContent>
    </Dialog>
  );
}